import {
  object,
  array,
  string,
  number,
  mixed,
  AnySchema,
  SchemaOf,
  StringSchema,
} from 'yup';

import {
  INVALID_NAMESPACE_NAME_MESSAGE,
  isValidNamespaceName,
} from '@/react/portainer/policies/utils/namespace';

import {
  ExpressionOperatorType,
  KubernetesNetworkSecurityFormValues,
  MatchExpression,
  Namespace,
  NetworkSecurityRule,
  PodSelection,
  PodSelectionType,
  TrafficSourceType,
} from './types';

const namespaceArraySchema: SchemaOf<Namespace> = object({
  namespace: string()
    .required()
    .test('valid-namespace', INVALID_NAMESPACE_NAME_MESSAGE, (value) =>
      isValidNamespaceName(value ?? '')
    ),
});

function objectOf<T extends AnySchema>(schema: T) {
  return {
    name: 'objectOf',
    exclusive: false,
    message: "Object values don't match the given schema",
    test: (value: null | Record<string, unknown>) => {
      if (value === undefined) {
        return true;
      }
      return (
        value === null ||
        Object.values(value).every((itemValue) => schema.isValidSync(itemValue))
      );
    },
  };
}

const matchExpressionsSchema: SchemaOf<MatchExpression> = object({
  key: string().required(),
  operator: mixed<ExpressionOperatorType>()
    .oneOf(Object.values(ExpressionOperatorType))
    .required(),
  values: array(string().required()),
});

const podSelectionSchema: SchemaOf<PodSelection> = object({
  type: mixed<PodSelectionType>()
    .oneOf(Object.values(PodSelectionType))
    .required(),
  matchLabels: object().test(objectOf(string())).optional(),
  matchExpressions: array(matchExpressionsSchema.required()).optional(),
});

const rulesSchema: SchemaOf<NetworkSecurityRule> = object({
  name: string().required(),
  appliesTo: podSelectionSchema.required(),
  sourceType: mixed<TrafficSourceType>()
    .oneOf(Object.values(TrafficSourceType))
    .required(),
  namespace: string()
    .optional()
    .test(
      'valid-namespace',
      INVALID_NAMESPACE_NAME_MESSAGE,
      (value) => !value || isValidNamespaceName(value)
    ),
  podLabels: object().test(objectOf(string())).optional(),
  cidr: string().optional(),
  port: number().min(1).max(65535).optional(),
  protocol: mixed().oneOf(['TCP', 'UDP']).optional(),
});

export function createKubernetesNetworkSecurityValidationSchema(
  nameValidation: StringSchema<string>
): SchemaOf<KubernetesNetworkSecurityFormValues> {
  return object({
    // PolicyConfigFormValues
    name: nameValidation,
    environmentGroupIds: array(number().required()),

    // Rules
    namespaces: array(namespaceArraySchema)
      .min(1, 'At least one namespace is required')
      .required('At least one namespace is required')
      .test(
        'unique-namespaces',
        'Namespaces must be unique',
        function validate(entries) {
          const list = entries ?? [];
          const seen = new Set<string>();
          for (let i = 0; i < list.length; i += 1) {
            const namespace = list[i]?.namespace?.trim() ?? '';
            if (namespace) {
              if (seen.has(namespace)) {
                return this.createError({
                  path: `${this.path}[${i}].namespace`,
                  message: `"${namespace}" is already configured`,
                });
              }
              seen.add(namespace);
            }
          }
          return true;
        }
      ),
    ingressRules: array(rulesSchema)
      .test('unique-rules', 'Rules must be unique', function validate(entries) {
        const list = entries ?? [];
        const seen = new Set<string>();
        for (let i = 0; i < list.length; i += 1) {
          const name = list[i]?.name?.trim() ?? '';
          if (name) {
            if (seen.has(name)) {
              return this.createError({
                path: `${this.path}[${i}].name`,
                message: `"${name}" is already used`,
              });
            }
            seen.add(name);
          }
        }
        return true;
      })
      .test(
        'valid-names',
        'Rule names must be lowercase and without spaces',
        function validate(entries) {
          const list = entries ?? [];
          for (let i = 0; i < list.length; i += 1) {
            const name = list[i]?.name ?? '';
            const trimmed = name.trim() ?? '';
            if (!trimmed) {
              return this.createError({
                path: `${this.path}[${i}].name`,
                message: `"${trimmed}" cannot be blank`,
              });
            }
            if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(name)) {
              return this.createError({
                path: `${this.path}[${i}].name`,
                message: `"${name}" must consist of lowercase alphanumeric characters or hyphens, and must start and end with an alphanumeric character`,
              });
            }
          }
          return true;
        }
      ),
    egressRules: array(rulesSchema)
      .test('unique-rules', 'Rules must be unique', function validate(entries) {
        const list = entries ?? [];
        const seen = new Set<string>();
        for (let i = 0; i < list.length; i += 1) {
          const name = list[i]?.name?.trim() ?? '';
          if (name) {
            if (seen.has(name)) {
              return this.createError({
                path: `${this.path}[${i}].name`,
                message: `"${name}" is already used`,
              });
            }
            seen.add(name);
          }
        }
        return true;
      })
      .test(
        'valid-names',
        'Rule names must be lowercase and without spaces',
        function validate(entries) {
          const list = entries ?? [];
          for (let i = 0; i < list.length; i += 1) {
            const name = list[i]?.name ?? '';
            const trimmed = name.trim() ?? '';
            if (!trimmed) {
              return this.createError({
                path: `${this.path}[${i}].name`,
                message: `"${trimmed}" cannot be blank`,
              });
            }
            if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(name)) {
              return this.createError({
                path: `${this.path}[${i}].name`,
                message: `"${name}" must consist of lowercase alphanumeric characters or hyphens, and must start and end with an alphanumeric character`,
              });
            }
          }
          return true;
        }
      ),
  });
}
